import { apiClient } from "@/http";
import type { MemberRef } from "./member";
import { apiRoutes } from "./apiRoutes";

// 한도 상향 요청은 registry 가 소유하는 레코드다. 요청은 기간이 있는 임시 증액이다. admin 이
// 승인하면 registry 가 training-infra 에 증액을 반영한다. 누가 요청했고 누가 승인했는지는 이
// 레코드만 가진다.

// 증액 기간의 선택지(일)다. 서버는 이 네 값만 받는다(그 외 422).
export const TRAINING_GPU_QUOTA_REQUEST_DAYS = [7, 14, 21, 28] as const;
export type TrainingGpuQuotaRequestDays = (typeof TRAINING_GPU_QUOTA_REQUEST_DAYS)[number];

// 상태는 pending 에서만 다른 상태로 바뀐다. 종결 상태는 이력이라 되돌리지 않는다.
export type TrainingGpuQuotaRequestStatus = "pending" | "applied" | "rejected" | "cancelled";

export type TrainingGpuQuotaRequest = {
  uuid: string;
  training_workspace_id: string;
  // registry 별칭이 없으면 null 이다. 그러면 UUID 로 폴백한다(쿼터·사용량 view 와 같은 규약).
  workspace_display_name: string | null;
  requested_gpu_limit: number;
  // 요청한 증액 기간(일). 대기 중인 요청에는 항상 값이 있다. null 은 기간 없이 종결된 옛
  // 요청이다. 그 요청에는 증액이 없다. 실제로 반영된 기간은 grant_start_at·grant_end_at 이 말한다.
  requested_days: number | null;
  // 승인할 때 서버가 확인한 training-infra 증액의 실제 기간이다. 승인이 남아 있던 증액을 다시 쓰면
  // 시작이 resolved_at 과 다르다. 그래서 만료일은 resolved_at 에 기간을 더하지 않고 grant_end_at 을
  // 쓴다. 승인되지 않은 요청과 이 값이 기록되지 않은 옛 승인 요청은 둘 다 null 이다.
  grant_start_at: string | null;
  grant_end_at: string | null;
  reason: string;
  status: TrainingGpuQuotaRequestStatus;
  // 대상 workspace 를 **지금** 선점한 사람. 선점자가 없으면 null 이다. 접수자(requested_by*)와
  // 다른 축이다. admin 이 대신 접수했거나 이후 선점자가 바뀌면 두 값이 다르다.
  // "누구 몫인가"는 이 값이고 "누가 신청했나"는 requested_by 다.
  owner_member: MemberRef | null;
  requested_by_member_uuid: string | null;
  requested_by: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  // 서버가 계산한 요청자 기준 가능 동작. 표시명을 비교해 "내 요청인지" 추측하지 않는다.
  // 동명이인이면 추측이 틀리고, 사용자는 버튼을 눌러 403 을 받게 된다.
  can_cancel: boolean;
  can_resolve: boolean;
};

export type TrainingGpuQuotaRequestCreate = {
  trainingWorkspaceId: string;
  // 증액 기간 동안의 한도. 1 이상이다.
  requestedGpuLimit: number;
  requestedDays: TrainingGpuQuotaRequestDays;
  reason: string;
};

export type TrainingGpuQuotaRequestListQuery = {
  // 운영자 대기열은 "pending" 이다. 어휘 밖 값은 422 다.
  status?: TrainingGpuQuotaRequestStatus;
  // 좁히기는 행에 고정된 접수자를 기준으로 한다. 선점자로 좁히면 어드민 재매핑 한 번으로
  // 과거 이력 전체의 귀속이 바뀐다. 같은 행의 can_cancel(요청자 기준)과도 맞지 않는다.
  // 없는 member uuid 는 404 가 아니라 빈 목록이다.
  requestedByMemberUuid?: string;
};

// 종결은 승인(applied) 또는 거절(rejected) 둘 중 하나다. 승인하면 서버가 증액을 반영한 뒤에
// applied 로 바꾼다. 철회(cancelled)는 요청자 전용 경로다. 그래서 이 본문으로 보낼 수 없다(422).
export type TrainingGpuQuotaRequestResolution = "applied" | "rejected";

export async function listTrainingGpuQuotaRequests(
  query: TrainingGpuQuotaRequestListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<TrainingGpuQuotaRequest[]> {
  const response = await apiClient.get<TrainingGpuQuotaRequest[]>(
    apiRoutes.training.gpuQuotaRequests,
    {
      params: {
        ...(query.status === undefined ? {} : { status: query.status }),
        ...(query.requestedByMemberUuid === undefined
          ? {}
          : { requested_by_member_uuid: query.requestedByMemberUuid }),
      },
      signal: options.signal,
    },
  );
  return response.data;
}

export async function createTrainingGpuQuotaRequest(
  payload: TrainingGpuQuotaRequestCreate,
): Promise<TrainingGpuQuotaRequest> {
  const response = await apiClient.post<TrainingGpuQuotaRequest>(
    apiRoutes.training.gpuQuotaRequests,
    {
      training_workspace_id: payload.trainingWorkspaceId,
      requested_gpu_limit: payload.requestedGpuLimit,
      requested_days: payload.requestedDays,
      reason: payload.reason,
    },
  );
  return response.data;
}

export async function cancelTrainingGpuQuotaRequest(
  requestUuid: string,
): Promise<TrainingGpuQuotaRequest> {
  const response = await apiClient.post<TrainingGpuQuotaRequest>(
    apiRoutes.training.gpuQuotaRequestCancel(requestUuid),
  );
  return response.data;
}

export async function resolveTrainingGpuQuotaRequest(
  requestUuid: string,
  payload: { status: TrainingGpuQuotaRequestResolution; resolutionNote?: string },
): Promise<TrainingGpuQuotaRequest> {
  const response = await apiClient.post<TrainingGpuQuotaRequest>(
    apiRoutes.training.gpuQuotaRequestResolve(requestUuid),
    {
      status: payload.status,
      ...(payload.resolutionNote === undefined ? {} : { resolution_note: payload.resolutionNote }),
    },
  );
  return response.data;
}
